<template>
    <component :is="tag" class="tariff-card" :class="classes">
        <div class="tariff-card__inner">
            <div class="tariff-card__header">
                <div class="tariff-card__name">
                    {{ tariff.title }}
                </div>
                <div v-if="tariff.subtitle" class="tariff-card__tip">
                    {{ tariff.subtitle }}
                </div>
            </div>

            <div class="tariff-card__description" v-html="tariff.description" />

            <div class="tariff-card__prices">
                <template v-if="hasDiscountPrice">
                    <div class="tariff-card__price-crossed">
                        <span>{{ tariff.price.fullPrice }}</span> ₽
                    </div>
                    <div class="tariff-card__price">
                        <span>{{ discountPriceTemplate }}</span> / мес
                    </div>
                </template>
                <template v-else>
                    <div class="tariff-card__price">
                        <span>{{ tariff.price.fullPrice }} ₽</span> / мес
                    </div>
                </template>
            </div>

            <UiButton class="tariff-card__button">
                Оплатить
            </UiButton>
        </div>

        <div class="tariff-card__footer">
            <ul class="tariff-card__benefits">
                <li v-for="benefit in tariff.benefits" :key="benefit" class="tariff-card__benefit">
                    {{ benefit }}
                </li>
            </ul>

            <NuxtLink class="tariff-card__link" to="/public">
                Подробнее
            </NuxtLink>
        </div>
    </component>
</template>

<script setup lang="ts">
    import type { TariffItem } from '~/src/shared/components/pages/index/mock/indexTariff'
    import { splitThousands } from '~/src/shared/utils/numbers-utils'

    const props = withDefaults(defineProps<{
        tariff: TariffItem
        subscriptionIndex?: number
        tag?: 'li' | 'div'
        stretchOnMobile?: boolean
    }>(), {
        tag: 'div'
    })

    const classes = computed(() => ({
        _primary: props.tariff.primary,
        '_mobile-stretch': props.stretchOnMobile
    }))

    const discountPrice = computed(() => {
        if (props.subscriptionIndex) {
            return props.tariff.price.discountPrice[props.subscriptionIndex] as number
        }

        return 0
    })
    const hasDiscountPrice = computed(() => discountPrice.value > 0)
    const discountPriceTemplate = computed(() => `${splitThousands(discountPrice.value)} ₽`)
</script>

<style scoped lang="scss">
    .tariff-card {
        $tariff: &;

        flex-shrink: 0;
        width: 311px;
        padding: 8px;
        border-radius: 24px;
        background-color: var(--neutral-100);

        &._primary {
            background-color: var(--blue-600);

            #{$tariff}__benefit {
                color: var(--white);
            }

            #{$tariff}__link {
                color: var(--white);
            }
        }

        &._mobile-stretch {
            @include respond-to(mobile) {
                width: 325px;
            }
        }

        &__inner {
            padding: 24px;
            border-radius: 20px;
            background-color: var(--white);
        }

        &__header {
            display: flex;
            align-items: center;
        }

        &__name {
            @include mulish24b;
        }

        &__tip {
            margin-left: 12px;
            padding: 8px 12px;
            border-radius: 20px;
            background-color: var(--neutral-100);
            font-family: var(--accent-font), sans-serif;
            font-size: 13px;
            font-weight: 600;
            line-height: 1;
        }

        &__description {
            @include mulish18b;

            margin-top: 32px;
            color: var(--grey-400);

            :deep(span) {
                color: var(--black-color);
            }
        }

        &__prices {
            display: flex;
            align-items: flex-start;
            flex-flow: column;
            margin-top: 24px;
        }

        &__price {
            @include mulish22r;

            display: flex;
            align-items: center;

            span {
                @include mulish32b;

                margin-right: 8px;
            }
        }

        &__price-crossed {
            margin-bottom: 8px;
            font-family: var(--accent-font), sans-serif;
            font-size: 18px;
            font-weight: 500;
            line-height: 1;
            color: var(--grey-400);

            span {
                position: relative;
                display: inline-block;

                &:after {
                    content: "";
                    position: absolute;
                    top: 50%;
                    left: 0;
                    width: 100%;
                    height: 1px;
                    background-color: currentcolor;
                    transform: rotate(-15deg) translateY(-50%);
                }
            }
        }

        &__button {
            margin-top: 32px;
        }

        &__footer {
            display: flex;
            align-items: flex-start;
            flex-flow: column;
            margin-top: 10px;
            padding: 24px 28px 16px;
        }

        &__benefit {
            position: relative;
            padding-left: 19px;
            color: var(--neutral-600);

            @include manrope17r;

            &:before {
                content: "";
                position: absolute;
                top: 50%;
                left: 0;
                width: 7px;
                height: 7px;
                border-radius: 50%;
                background-color: currentcolor;
                transform: translateY(-50%);
            }
        }

        &__link {
            position: relative;
            display: inline-flex;
            margin: 28px auto 0;
            font-family: var(--base-font), sans-serif;
            font-size: 16px;
            font-weight: 500;line-height: 1;
            color: var(--neutral-400);

            &:before {
                content: "";
                position: absolute;
                top: 100%;
                left: 0;
                width: 100%;
                height: 1px;
                background-color: currentcolor;
            }
        }
    }
</style>
